import { ComponentRef, FormEvent, ReactElement, useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Alert, Button, ErrorSummary, Heading, Paragraph } from '@digdir/designsystemet-react';
import { ShieldLockIcon } from '@navikt/aksel-icons';
import type { Survey } from '../../api/queries';
import { useFormSubmissionMutation } from '../../hooks';
import { useGetSurveyByUUIDQuery } from '../../hooks/queries/useGetSurveyByUUIDQuery.ts';
import { useLanguageSynchronization } from '../../hooks/useLanguageSynchronization';
import { FormSubmissionUtils } from './formSubmission.utils.ts';
import { QuestionCard } from './components/QuestionCard';
import { SurveyClosed } from './components/SurveyClosed';
import { SurveyHeader } from './components/SurveyHeader';
import { SurveyThankYou } from './components/SurveyThankYou';
import { answeredCount, missingQuestions, scaleLabels, surveyResponse, toQuestionCard } from './psychologicalSafetyFormView';
import { Answers, questionElementId } from './workEnvironmentFormView';
import classes from './WorkEnvironmentFormSubmission.module.css';

export const FormSubmission = (): ReactElement | null => {
  const { guid } = useParams();
  const { handleLanguageChange } = useLanguageSynchronization();
  const { data: survey, isError, isLoading } = useGetSurveyByUUIDQuery(guid!, { enabled: Boolean(guid) });
  const submit = useFormSubmissionMutation();

  if (isError && !survey) return <SurveyClosed reason="loadFailed" onLanguageChange={handleLanguageChange} />;
  if (!guid || isLoading || !survey) return null;
  if (!submit.isSuccess && (!survey.isActive || FormSubmissionUtils.hasAnswered(survey.uuid))) {
    // Answers are anonymous, so only this browser knows it answered. Otherwise the survey has closed.
    const reason = FormSubmissionUtils.hasAnswered(survey.uuid) ? 'alreadyAnswered' : 'expired';
    return <SurveyClosed reason={reason} onLanguageChange={handleLanguageChange} />;
  }

  return (
    <SurveyForm
      survey={survey}
      onLanguageChange={handleLanguageChange}
      submit={{
        run: (answers) =>
          submit.mutate(surveyResponse(survey, answers), {
            onSuccess: () => FormSubmissionUtils.addNewAnsweredSurvey(survey.uuid),
          }),
        pending: submit.isPending,
        done: submit.isSuccess,
        failed: submit.isError,
      }}
    />
  );
};

type SurveyFormProps = {
  survey: Survey;
  onLanguageChange: (language: string) => void;
  submit: {
    run: (answers: Answers) => void;
    pending: boolean;
    done: boolean;
    failed: boolean;
  };
};

const SurveyForm = ({ survey, onLanguageChange, submit }: SurveyFormProps): ReactElement => {
  const { t } = useTranslation();
  const [answers, setAnswers] = useState<Answers>({});
  const [tried, setTried] = useState(false);
  const [triedCount, setTriedCount] = useState(0);
  const mainRef = useRef<HTMLElement>(null);
  const summaryRef = useRef<ComponentRef<typeof ErrorSummary>>(null);

  const questions = survey.questions.map(toQuestionCard);
  const missing = missingQuestions(survey, answers);
  const total = questions.length;
  const answered = answeredCount(survey, answers);

  useEffect(() => {
    if (triedCount > 0) summaryRef.current?.focus();
  }, [triedCount]);

  useEffect(() => {
    if (!submit.done) return;
    window.scrollTo(0, 0);
    mainRef.current?.focus({ preventScroll: true });
  }, [submit.done]);

  const onSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (missing.length > 0) {
      setTried(true);
      setTriedCount((count) => count + 1);
      return;
    }
    submit.run(answers);
  };

  return (
    <div className={classes.page} data-size="md">
      <a href="#main" className={`ds-link ${classes.skipLink}`}>
        {t('workEnvironmentFormSubmission.skipToQuestions')}
      </a>
      <SurveyHeader
        progress={submit.done ? 100 : total > 0 ? (answered / total) * 100 : 0}
        onLanguageChange={onLanguageChange}
      />
      <main id="main" ref={mainRef} tabIndex={-1} className={classes.main}>
        <div aria-hidden="true" className={classes.backdrop} />
        {submit.done ? (
          <SurveyThankYou />
        ) : (
          <div className={classes.content}>
            <div className={classes.intro}>
              <span className={`ds-label ${classes.eyebrow}`} data-size="sm">
                {t('formSubmission.eyebrow')}
              </span>
              <Heading level={1} data-size="xl" className={classes.title}>
                {survey.title}
              </Heading>
              <Paragraph className={classes.introText}>{t('formSubmission.intro')}</Paragraph>
              <div className={classes.anonymous}>
                <ShieldLockIcon aria-hidden />
                <Paragraph data-size="sm">{t('workEnvironmentFormSubmission.anonymous')}</Paragraph>
              </div>
            </div>

            <form noValidate onSubmit={onSubmit} className={classes.form}>
              {tried && missing.length > 0 && (
                <ErrorSummary ref={summaryRef}>
                  <ErrorSummary.Heading>{t('formSubmission.errorSummary')}</ErrorSummary.Heading>
                  <ErrorSummary.List>
                    {missing.map((question) => (
                      <ErrorSummary.Item key={question.ref}>
                        <ErrorSummary.Link href={`#${questionElementId(question.ref)}`}>
                          {question.text}
                        </ErrorSummary.Link>
                      </ErrorSummary.Item>
                    ))}
                  </ErrorSummary.List>
                </ErrorSummary>
              )}

              <section className={classes.part}>
                {questions.map((question, index) => (
                  <QuestionCard
                    key={question.ref}
                    question={question}
                    labels={scaleLabels(t)}
                    number={index + 1}
                    total={total}
                    value={answers[question.ref]}
                    onChange={(value) => setAnswers((current) => ({ ...current, [question.ref]: value }))}
                    invalid={tried && answers[question.ref] === undefined}
                  />
                ))}
              </section>

              {submit.failed && (
                <Alert data-color="danger">
                  <Paragraph>{t('workEnvironmentFormSubmission.submitFailed')}</Paragraph>
                </Alert>
              )}

              <div className={classes.footer}>
                <Paragraph data-size="sm" role="status" className={classes.subtle}>
                  {t('workEnvironmentFormSubmission.answered', { answered, total })}
                </Paragraph>
                <Button
                  type="submit"
                  variant="primary"
                  data-size="lg"
                  className={classes.nextButton}
                  loading={submit.pending}
                >
                  {t('workEnvironmentFormSubmission.send')}
                </Button>
              </div>
            </form>
          </div>
        )}
      </main>
    </div>
  );
};
